/* tslint:disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';

// Modeles
import { ConfigDataModele } from './core/modeles/_index';
import { get, unionBy } from 'lodash';
import { DEFAULT_TOGGLES_CONFIG } from '../configs/toggles.config';
import { BehaviorSubject } from 'rxjs';
import { TypeProjet } from './shared/constants/type-projet.enum';
import { PersonneState } from './shared/personne.state';
import { ProvinceEnum } from './shared/constants/province.enum';

import { ConfigMeta, ConfigurationService } from '@oel/js-configuration';

const TYPE_PROJET_KEY = 'typeProjet';
const DEFAULT_LANGUE = 'fr';
const DEFAULT_URL_RETOUR = 'https://desjardins.com';

@Injectable()
export class AppConfigService {
  private _configData: ConfigDataModele;
  private headersGET = new HttpHeaders({
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0'
  });

  isConfigLoaded$ = new BehaviorSubject(false);
  get isConfigLoaded() {
    return this.isConfigLoaded$.value;
  }

  private _isPDOFusionPending = false;
  private _donneReference: Object;
  private _typeProjet: TypeProjet;
  private _idProjet: string;

  constructor(private readonly _httpClient: HttpClient) {
    this.initTypeProjet();
  }

  private initTypeProjet() {
    this._typeProjet = (new URLSearchParams(window.location.search).get('projet') ||
      sessionStorage.getItem(TYPE_PROJET_KEY) ||
      TypeProjet.PremierAcheteur) as TypeProjet;

    sessionStorage.setItem(TYPE_PROJET_KEY, this._typeProjet);
  }

  private fetchData<T>(endpoint: string) {
    const passApiUrl = ConfigurationService.getConfig(ConfigMeta.OEL_PAAS_API_URL);
    return this._httpClient
      .get<T>(`${passApiUrl}${endpoint}`, { headers: this.headersGET })
      .toPromise();
  }

  /**
   * @description Fonction utiliser pendant l'initialisation du SPA ,
   * pour appeler le BFF au endpoint [api/contextes/session] pour recuperer l'information necessaire pour builder le SPA.
   */
  async loadAppConfig() {
    try {
      this._configData = await this.fetchData(`/api/contextes/session?typeProjet=${this.typeProjet}`);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 410) {
        this._isPDOFusionPending = true;
      } else {
        throw error;
      }
    }

    if (!this._configData.contexteEnvironnement.langue) {
      this._configData.contexteEnvironnement.langue = DEFAULT_LANGUE;
    }

    this._idProjet = this.getIdProjet();
    this._configData.toggles = unionBy(this._configData.toggles, DEFAULT_TOGGLES_CONFIG, 'name');
    this.isConfigLoaded$.next(true);
    this.isConfigLoaded$.complete();
  }

  loadDefaultConfig() {
    this._configData = {
      contexteEnvironnement: {
        urlRetour: DEFAULT_URL_RETOUR,
        app: PersonneState.detectCanal() === 'Mobile',
        langue: DEFAULT_LANGUE
      },
      toggles: DEFAULT_TOGGLES_CONFIG
    };

    this.isConfigLoaded$.next(true);
    this.isConfigLoaded$.complete();
  }

  async loadAppDonneReference() {
    this._donneReference = await this.fetchData('/api/v1/references');
  }

  get configData(): ConfigDataModele {
    return this._configData;
  }

  get typeProjet(): TypeProjet {
    return this._typeProjet;
  }

  get idProjet(): string {
    return this._idProjet;
  }

  private getIdProjet(): string {
    if (this.configData.nonEligible) {
      return null;
    }

    const projet = get(this.configData, 'personne.projetsActifs', []).find(
      projet => projet.typeProjet === this.typeProjet
    );

    if (projet && projet.id) {
      return projet.id;
    }

    throw new Error("Le projet n'existe pas");
  }

  get donneReference(): Object {
    return this._donneReference;
  }

  get langue(): string {
    return this.isConfigLoaded ? this._configData.contexteEnvironnement.langue : 'fr';
  }

  get isApp(): boolean {
    return this.isConfigLoaded ? this._configData.contexteEnvironnement.app : PersonneState.detectCanal() === 'Mobile';
  }

  get isPDOFusionPending(): boolean {
    return this._isPDOFusionPending;
  }

  get urlRetour(): string {
    if (this.isConfigLoaded) {
      return (
        this._configData.contexteEnvironnement.urlRetourMonToit || this._configData.contexteEnvironnement.urlRetour
      );
    } else {
      return DEFAULT_URL_RETOUR;
    }
  }

  get province(): ProvinceEnum {
    const institution = get(this._configData, 'personne.institution')
      ? get(this._configData, 'personne.institution')
      : get(this._configData, 'contexteAchat.institution', '815');

    if (institution === '815') {
      return ProvinceEnum.Quebec;
    } else {
      return ProvinceEnum.Ontario;
    }
  }

  simulateFusionPdoDebugOnly() {
    this._isPDOFusionPending = true;
  }
}
